/**
 * URL utility functions for parsing and extracting information from URLs.
 */

/**
 * Extracts the app name from a URL pathname following the pattern /app/{appname}/...
 *
 * @param {string} pathname - The pathname from window.location or useLocation
 * @returns {string|null} - The extracted app name or null if not found
 *
 * @example
 * extractAppNameFromPath('/app/dispatch/2025-07-21') // returns 'dispatch'
 * extractAppNameFromPath('/app/timekeeping/some-page') // returns 'timekeeping'
 * extractAppNameFromPath('/other/path') // returns null
 */
export function extractAppNameFromPath(pathname) {
  if (!pathname || typeof pathname !== "string") {
    return null;
  }

  const pathSegments = pathname.split("/");

  if (pathSegments[3]) {
    return pathSegments[3];
  }
  return null;
}

/**
 * Extracts the domain from a URL pathname following the pattern /app/{domain}/...
 *
 * @param {string} pathname - The pathname from window.location or useLocation.
 * @returns {string|null} The extracted domain or null if not found.
 *
 * @example
 * extractDomainFromPath('/app/dispatch/2025-07-21') // returns 'dispatch'
 * extractDomainFromPath('/app/timekeeping/some-page') // returns 'timekeeping'
 * extractDomainFromPath('/other/path') // returns null
 */
export function extractDomainFromPath(pathname) {
  if (!pathname || typeof pathname !== "string") {
    return null;
  }

  const pathSegments = pathname.split("/");

  if (pathSegments[2]) {
    return pathSegments[2];
  }
  return null;
}

/** Display strings that are not real street addresses and must not open Maps. */
const UNMAPPABLE_ADDRESS_LABELS = new Set(["no address set"]);

/**
 * Build a Google Maps search URL for an address string.
 *
 * Desktop opens Maps in a new tab; mobile typically hands off to the Maps app.
 *
 * @param {string|null|undefined} address Address text from a job or scope.
 * @returns {string|null} Encoded Maps search URL, or null when the value is empty or a placeholder.
 */
export function getGoogleMapsSearchUrl(address) {
  if (typeof address !== "string") {
    return null;
  }
  const trimmed = address.trim();
  if (!trimmed || UNMAPPABLE_ADDRESS_LABELS.has(trimmed.toLowerCase())) {
    return null;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
}
